import { useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, Polyline, Popup, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import { FiArchive, FiCheck, FiEdit3, FiMap, FiPlus, FiTrash2, FiX } from 'react-icons/fi';
import type { AuthFetch, Order, Periodo, Route, Session, User } from '../../types';
import { fmtDate, fmtDist, fmtDur } from '../../lib/format';
import {
  DEFAULT_CENTER,
  PERIODOS,
  PER_COLOR,
  PER_LABEL,
  STATUS_COLOR,
  STATUS_LABEL,
  geocodeNominatim,
  optimizeWithPeriods,
  sleep,
  GEOCODE_DELAY
} from '../../lib/routes';
import { cn } from '../../lib/cn';

/* ─── ícones leaflet ─────────────────────────────────────── */
function orderIcon(num: number, color = '#f59e0b') {
  return L.divIcon({
    className: '',
    html: `<div style="background:${color};color:#fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;box-shadow:0 1px 4px rgba(0,0,0,.4)">${num}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14]
  });
}

interface RoutesPageProps {
  authFetch: AuthFetch;
  session: Session;
  users: User[];
}

export default function RoutesPage({ authFetch, session, users }: RoutesPageProps) {
  const isAdmin = session?.type === 'admin';

  const [routes, setRoutes] = useState<Route[]>([]);
  const [subTab, setSubTab] = useState<'ativas' | 'antigas'>('ativas');
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState<Route | null>(null);

  const [fTech, setFTech] = useState('');
  const [fDate, setFDate] = useState('');
  const [fStart, setFStart] = useState('');
  const [fOrders, setFOrders] = useState<Order[]>([]);
  const [fRouteOrder, setFRouteOrder] = useState<number[]>([]);
  const [fGeometry, setFGeometry] = useState<Route['geometry']>(null);
  const [fDist, setFDist] = useState(0);
  const [fDur, setFDur] = useState(0);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeMsg, setGeocodeMsg] = useState('');
  const [optimizing, setOptimizing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [osCod, setOsCod] = useState('');
  const [osNome, setOsNome] = useState('');
  const [osEnd, setOsEnd] = useState('');
  const [osCidade, setOsCidade] = useState('Sorocaba - SP');
  const [osPeriodo, setOsPeriodo] = useState<Periodo>('comercial');

  const [viewRoute, setViewRoute] = useState<Route | null>(null);

  const techOptions = useMemo(
    () =>
      (users || [])
        .filter((u) => u.ativo !== false)
        .map((u) => u.username)
        .sort(),
    [users]
  );

  const loadRoutes = useCallback(async () => {
    setLoading(true);
    try {
      const query = isAdmin ? `?status=${subTab === 'antigas' ? 'completed' : 'active'}` : '';
      const data = await authFetch<Route[]>(`/routes${query}`);
      setRoutes(Array.isArray(data) ? data : []);
    } catch (err) {
      window.alert('Erro ao carregar rotas: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, isAdmin, subTab]);

  useEffect(() => {
    loadRoutes();
  }, [loadRoutes]);

  const openCreate = () => {
    setEditingRoute(null);
    setFTech(techOptions[0] || '');
    setFDate(new Date().toISOString().slice(0, 10));
    setFStart('');
    setFOrders([]);
    setFRouteOrder([]);
    setFGeometry(null);
    setFDist(0);
    setFDur(0);
    setGeocodeMsg('');
    setModalOpen(true);
  };

  const openEdit = (route: Route) => {
    setEditingRoute(route);
    setFTech(route.technicianUsername);
    setFDate(route.date);
    setFStart(route.startPoint?.address || '');
    setFOrders(route.orders.map((o) => ({ ...o })));
    setFRouteOrder(route.routeOrder || []);
    setFGeometry(route.geometry || null);
    setFDist(route.totalDistance || 0);
    setFDur(route.totalDuration || 0);
    setGeocodeMsg('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRoute(null);
  };

  const addOrder = () => {
    if (!osCod.trim()) return window.alert('Informe o código da OS.');
    if (!osEnd.trim()) return window.alert('Informe o endereço da OS.');
    setFOrders((prev) => [
      ...prev,
      {
        cod: osCod.trim(),
        nome: osNome.trim(),
        enderecoRaw: osEnd.trim(),
        cidade: osCidade,
        periodo: osPeriodo,
        lat: null,
        lng: null,
        source: '',
        status: 'pending',
        notes: ''
      }
    ]);
    setOsCod('');
    setOsNome('');
    setOsEnd('');
    setFRouteOrder([]);
    setFGeometry(null);
  };

  const removeOrder = (idx: number) => {
    setFOrders((prev) => prev.filter((_, i) => i !== idx));
    setFRouteOrder([]);
    setFGeometry(null);
  };

  const runGeocode = async () => {
    if (fOrders.length === 0) return;
    setGeocoding(true);
    setGeocodeMsg('');
    const updated = fOrders.map((o) => ({ ...o }));
    for (let i = 0; i < updated.length; i++) {
      if (updated[i].lat && updated[i].lng) continue;
      setGeocodeMsg(`Geocodificando ${i + 1}/${updated.length}: ${updated[i].cod}…`);
      try {
        const c = await geocodeNominatim(updated[i].enderecoRaw, updated[i].cidade);
        if (c) {
          updated[i].lat = c.lat;
          updated[i].lng = c.lng;
          updated[i].source = 'nominatim';
          updated[i].status = 'ok';
        } else {
          updated[i].status = 'pending';
        }
      } catch {
        updated[i].status = 'pending';
      }
      if (i < updated.length - 1) await sleep(GEOCODE_DELAY);
    }
    const ok = updated.filter((o) => o.lat).length;
    setGeocodeMsg(`Concluído: ${ok}/${updated.length} endereços encontrados.`);
    setFOrders(updated);
    setGeocoding(false);
    setFRouteOrder([]);
    setFGeometry(null);
  };

  const runOptimize = async () => {
    const withCoords = fOrders.filter((o) => o.lat && o.lng);
    if (withCoords.length < 1) return window.alert('Geocodifique os endereços primeiro.');
    setOptimizing(true);
    try {
      const startCoords = fStart ? await geocodeNominatim(fStart, 'Brasil').catch(() => null) : null;
      const result = await optimizeWithPeriods(withCoords, startCoords);
      setFRouteOrder(result.order);
      setFGeometry(result.geometry);
      setFDist(result.distance);
      setFDur(result.duration);
    } catch (err) {
      window.alert('Falha ao otimizar: ' + (err as Error).message);
    } finally {
      setOptimizing(false);
    }
  };

  const saveRoute = async () => {
    if (!fTech) return window.alert('Selecione o técnico.');
    if (!fDate) return window.alert('Informe a data.');
    if (fOrders.length === 0) return window.alert('Adicione ao menos uma OS.');
    setSaving(true);
    try {
      const body = {
        technicianUsername: fTech,
        date: fDate,
        title: `Rota ${fTech} — ${fDate}`,
        startPoint: { address: fStart, lat: null, lng: null },
        orders: fOrders,
        routeOrder: fRouteOrder,
        geometry: fGeometry,
        totalDistance: fDist,
        totalDuration: fDur
      };
      if (editingRoute) {
        await authFetch(`/routes/${editingRoute._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      } else {
        await authFetch('/routes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      }
      closeModal();
      loadRoutes();
    } catch (err) {
      window.alert('Erro ao salvar rota: ' + (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const deleteRoute = async (route: Route) => {
    if (!window.confirm(`Excluir a rota de ${route.technicianUsername} em ${fmtDate(route.date)}?`)) return;
    try {
      await authFetch(`/routes/${route._id}`, { method: 'DELETE' });
      loadRoutes();
      if (viewRoute?._id === route._id) setViewRoute(null);
    } catch (err) {
      window.alert('Erro ao excluir: ' + (err as Error).message);
    }
  };

  const mapOrders = useMemo(() => {
    if (!viewRoute) return [];
    if (viewRoute.routeOrder && viewRoute.routeOrder.length > 0) {
      return viewRoute.routeOrder.map((origIdx, pos) => ({ ...viewRoute.orders[origIdx], _pos: pos + 1 }));
    }
    return viewRoute.orders.map((o, i) => ({ ...o, _pos: i + 1 }));
  }, [viewRoute]);

  const routePolyline = useMemo<[number, number][] | null>(() => {
    if (!viewRoute?.geometry?.coordinates) return null;
    return viewRoute.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
  }, [viewRoute]);

  const formMapOrders = useMemo(() => {
    if (fRouteOrder.length > 0) {
      return fRouteOrder.map((origIdx, pos) => ({ ...fOrders[origIdx], _pos: pos + 1 }));
    }
    return fOrders.filter((o) => o.lat && o.lng).map((o, i) => ({ ...o, _pos: i + 1 }));
  }, [fOrders, fRouteOrder]);

  const formPolyline = useMemo<[number, number][] | null>(() => {
    if (!fGeometry?.coordinates) return null;
    return fGeometry.coordinates.map(([lng, lat]) => [lat, lng]);
  }, [fGeometry]);

  const inputCls =
    'w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20';

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      {/* Painel esquerdo */}
      <aside className="flex w-[340px] min-w-[300px] flex-col overflow-hidden border-r border-slate-200 bg-white sm:w-[380px]">
        <div className="flex items-center justify-between bg-slate-900 px-4 py-3 text-white">
          <div>
            <div className="text-[15px] font-bold">Módulo Rotas</div>
            <div className="text-[11px] text-slate-400">Ordens de Serviço</div>
          </div>
          {isAdmin && (
            <button
              onClick={openCreate}
              className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-1.5 text-[13px] font-bold text-white hover:bg-blue-700"
            >
              <FiPlus /> Nova Rota
            </button>
          )}
        </div>

        {isAdmin && (
          <div className="flex border-b border-slate-200">
            {(['ativas', 'antigas'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setSubTab(t)}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1.5 border-b-2 py-2 text-[13px] transition',
                  subTab === t
                    ? 'border-blue-600 bg-sky-50 font-bold text-blue-700'
                    : 'border-transparent font-normal text-slate-500 hover:bg-slate-50'
                )}
              >
                {t === 'antigas' ? <FiArchive size={14} /> : <FiMap size={14} />}
                {t === 'ativas' ? 'Ativas' : 'Rotas Antigas'}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="pt-8 text-center text-[13px] text-slate-400">Carregando rotas…</div>
          ) : routes.length === 0 ? (
            <div className="pt-8 text-center text-[13px] text-slate-400">
              {subTab === 'antigas' ? 'Nenhuma rota concluída.' : 'Nenhuma rota ativa.'}
            </div>
          ) : (
            routes.map((route) => {
              const done = route.orders.filter((o) => o.status === 'concluida' || o.status === 'reagendada').length;
              const total = route.orders.length;
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              const isViewing = viewRoute?._id === route._id;
              return (
                <div
                  key={route._id}
                  onClick={() => setViewRoute(isViewing ? null : route)}
                  className={cn(
                    'mb-2 cursor-pointer rounded-xl border p-3 transition',
                    isViewing ? 'border-blue-600 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[13px] font-bold text-slate-800">👤 {route.technicianUsername}</div>
                      <div className="text-xs text-slate-500">📅 {fmtDate(route.date)}</div>
                    </div>
                    {isAdmin && subTab === 'ativas' && (
                      <div className="flex gap-1.5">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(route);
                          }}
                          title="Editar"
                          className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
                        >
                          <FiEdit3 size={15} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteRoute(route);
                          }}
                          title="Excluir"
                          className="rounded-md p-1 text-red-600 hover:bg-red-50"
                        >
                          <FiTrash2 size={15} />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${pct}%`, background: pct === 100 ? '#16a34a' : '#2563eb' }}
                    />
                  </div>
                  <div className="mt-1 flex justify-between text-[11px] text-slate-500">
                    <span>
                      {done}/{total} OS concluídas
                    </span>
                    {route.totalDistance > 0 && (
                      <span>
                        {fmtDist(route.totalDistance)} · {fmtDur(route.totalDuration)}
                      </span>
                    )}
                  </div>
                  {isViewing && (
                    <div className="mt-2.5 border-t border-slate-200 pt-2">
                      {(route.routeOrder.length > 0 ? route.routeOrder.map((i) => route.orders[i]) : route.orders).map(
                        (o, pos) => (
                          <div key={pos} className="flex items-center gap-2 border-b border-slate-100 py-1 text-xs">
                            <span
                              className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
                              style={{ background: STATUS_COLOR[o.status] || '#94a3b8' }}
                            >
                              {pos + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="truncate font-semibold text-slate-800">
                                {o.cod} {o.nome && `— ${o.nome}`}
                              </div>
                              <div className="truncate text-[11px] text-slate-500">{o.enderecoRaw}</div>
                            </div>
                            <span className="whitespace-nowrap text-[10px] font-bold" style={{ color: STATUS_COLOR[o.status] }}>
                              {STATUS_LABEL[o.status]}
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </aside>

      {/* Mapa */}
      <div className="relative flex-1">
        {!viewRoute && (
          <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center">
            <div className="rounded-xl border border-slate-200 bg-white/90 px-6 py-4 text-center shadow-lg">
              <FiMap size={32} className="mx-auto text-slate-400" />
              <p className="mt-2 text-[13px] text-slate-500">Selecione uma rota para visualizar no mapa</p>
            </div>
          </div>
        )}
        <MapContainer
          center={viewRoute && mapOrders[0] ? [mapOrders[0].lat as number, mapOrders[0].lng as number] : DEFAULT_CENTER}
          zoom={13}
          className="h-full w-full"
          key={viewRoute?._id || 'empty'}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
          {mapOrders.map(
            (o, i) =>
              o.lat &&
              o.lng && (
                <Marker key={i} position={[o.lat, o.lng]} icon={orderIcon(o._pos, PER_COLOR[o.periodo] || '#f59e0b')}>
                  <Popup>
                    <b>{o.cod}</b> — {o.nome || '—'}
                    <br />
                    <small>
                      {o.enderecoRaw}
                      {o.cidade ? ` · ${o.cidade}` : ''}
                    </small>
                    <br />
                    <span style={{ color: PER_COLOR[o.periodo] }}>{PER_LABEL[o.periodo]}</span>
                    <br />
                    <span style={{ color: STATUS_COLOR[o.status], fontWeight: 700 }}>{STATUS_LABEL[o.status]}</span>
                    {o.notes && (
                      <>
                        <br />
                        <small style={{ color: '#64748b' }}>📝 {o.notes}</small>
                      </>
                    )}
                  </Popup>
                </Marker>
              )
          )}
          {routePolyline && <Polyline positions={routePolyline} color="#1d4ed8" weight={4} opacity={0.75} />}
        </MapContainer>
      </div>

      {/* Modal criar/editar rota */}
      {modalOpen && (
        <div className="fixed inset-0 z-[9999] flex">
          <div className="flex w-full max-w-[460px] flex-col overflow-hidden bg-white shadow-2xl">
            <div className="flex flex-shrink-0 items-center justify-between bg-slate-900 px-4.5 py-3.5 text-white">
              <span className="text-[15px] font-bold">{editingRoute ? 'Editar Rota' : 'Nova Rota'}</span>
              <button onClick={closeModal} className="text-slate-400 hover:text-white">
                <FiX size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="mb-3 grid grid-cols-2 gap-2.5">
                <label className="flex flex-col gap-1 text-xs font-semibold text-slate-700">
                  Técnico
                  <select value={fTech} onChange={(e) => setFTech(e.target.value)} className={inputCls}>
                    <option value="">Selecione</option>
                    {techOptions.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-xs font-semibold text-slate-700">
                  Data
                  <input type="date" value={fDate} onChange={(e) => setFDate(e.target.value)} className={inputCls} />
                </label>
              </div>

              <label className="mb-4 flex flex-col gap-1 text-xs font-semibold text-slate-700">
                Ponto de início (opcional)
                <input
                  value={fStart}
                  onChange={(e) => setFStart(e.target.value)}
                  placeholder="Endereço de partida"
                  className={inputCls}
                />
              </label>

              <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="mb-2 text-[13px] font-bold text-slate-800">Adicionar OS</div>
                <div className="mb-2 grid grid-cols-2 gap-2">
                  <input value={osCod} onChange={(e) => setOsCod(e.target.value)} placeholder="Código" className={cn(inputCls, 'text-xs')} />
                  <input
                    value={osNome}
                    onChange={(e) => setOsNome(e.target.value)}
                    placeholder="Nome do cliente"
                    className={cn(inputCls, 'text-xs')}
                  />
                </div>
                <input
                  value={osEnd}
                  onChange={(e) => setOsEnd(e.target.value)}
                  placeholder="Endereço"
                  className={cn(inputCls, 'mb-2 text-xs')}
                />
                <div className="mb-2 grid grid-cols-2 gap-2">
                  <input
                    value={osCidade}
                    onChange={(e) => setOsCidade(e.target.value)}
                    placeholder="Cidade"
                    className={cn(inputCls, 'text-xs')}
                  />
                  <select value={osPeriodo} onChange={(e) => setOsPeriodo(e.target.value as Periodo)} className={cn(inputCls, 'text-xs')}>
                    {PERIODOS.map((p) => (
                      <option key={p} value={p}>
                        {PER_LABEL[p]}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={addOrder}
                  className="w-full rounded-lg bg-blue-600 py-1.5 text-[13px] font-bold text-white hover:bg-blue-700"
                >
                  + Adicionar
                </button>
              </div>

              {fOrders.length > 0 && (
                <div className="mb-3">
                  <div className="mb-1.5 text-[13px] font-bold text-slate-800">Ordens ({fOrders.length})</div>
                  {fOrders.map((o, i) => (
                    <div key={i} className="mb-1 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5">
                      <span
                        className="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
                        style={{ background: o.lat ? PER_COLOR[o.periodo] : '#94a3b8' }}
                      >
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold">
                          {o.cod}
                          {o.nome ? ` — ${o.nome}` : ''}
                        </div>
                        <div className="truncate text-[11px] text-slate-500">{o.enderecoRaw}</div>
                      </div>
                      {o.lat ? (
                        <FiCheck size={13} className="text-emerald-600" />
                      ) : (
                        <span className="text-[10px] text-amber-500">sem coord</span>
                      )}
                      <button onClick={() => removeOrder(i)} className="text-red-600">
                        <FiX size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {fOrders.some((o) => !o.lat) && (
                <button
                  onClick={runGeocode}
                  disabled={geocoding}
                  className={cn(
                    'mb-2 w-full rounded-lg py-2 text-[13px] font-bold text-white',
                    geocoding ? 'cursor-not-allowed bg-slate-400' : 'bg-sky-600 hover:bg-sky-700'
                  )}
                >
                  {geocoding ? 'Validando endereços…' : '1. Validar endereços'}
                </button>
              )}
              {geocodeMsg && <div className="mb-2 text-xs text-slate-500">{geocodeMsg}</div>}

              {fOrders.some((o) => o.lat) && (
                <button
                  onClick={runOptimize}
                  disabled={optimizing}
                  className={cn(
                    'mb-2 w-full rounded-lg py-2 text-[13px] font-bold text-white',
                    optimizing ? 'cursor-not-allowed bg-slate-400' : 'bg-emerald-600 hover:bg-emerald-700'
                  )}
                >
                  {optimizing ? 'Otimizando…' : '2. Otimizar Rota'}
                </button>
              )}

              {fDist > 0 && (
                <div className="mb-3 grid grid-cols-2 gap-2">
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-center">
                    <div className="text-[10px] uppercase text-slate-500">Distância</div>
                    <div className="text-sm font-bold text-slate-800">{fmtDist(fDist)}</div>
                  </div>
                  <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-center">
                    <div className="text-[10px] uppercase text-slate-500">Duração</div>
                    <div className="text-sm font-bold text-slate-800">{fmtDur(fDur)}</div>
                  </div>
                </div>
              )}
            </div>

            {formMapOrders.some((o) => o.lat) && (
              <div className="h-[220px] flex-shrink-0 border-t border-slate-200">
                <MapContainer
                  center={[
                    formMapOrders.find((o) => o.lat)?.lat || DEFAULT_CENTER[0],
                    formMapOrders.find((o) => o.lat)?.lng || DEFAULT_CENTER[1]
                  ]}
                  zoom={13}
                  className="h-full w-full"
                  key={fOrders.length + String(fDist)}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  {formMapOrders.map(
                    (o, i) =>
                      o.lat &&
                      o.lng && (
                        <Marker key={i} position={[o.lat, o.lng]} icon={orderIcon(o._pos, PER_COLOR[o.periodo])}>
                          <Popup>
                            <b>{o.cod}</b>
                            <br />
                            {o.enderecoRaw}
                          </Popup>
                        </Marker>
                      )
                  )}
                  {formPolyline && <Polyline positions={formPolyline} color="#1d4ed8" weight={4} opacity={0.75} />}
                </MapContainer>
              </div>
            )}

            <div className="flex flex-shrink-0 gap-2.5 border-t border-slate-200 bg-slate-50 px-4 py-3">
              <button
                onClick={closeModal}
                className="flex-1 rounded-lg border border-slate-300 bg-white py-2 text-[13px] font-semibold text-slate-700 hover:bg-slate-100"
              >
                Cancelar
              </button>
              <button
                onClick={saveRoute}
                disabled={saving || fOrders.length === 0}
                className={cn(
                  'flex-[2] rounded-lg py-2 text-[13px] font-bold text-white',
                  saving || fOrders.length === 0 ? 'cursor-not-allowed bg-slate-400' : 'bg-emerald-600 hover:bg-emerald-700'
                )}
              >
                {saving ? 'Salvando…' : 'Salvar Rota'}
              </button>
            </div>
          </div>

          <div className="flex-1 bg-slate-900/50" onClick={closeModal} />
        </div>
      )}
    </div>
  );
}
